import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { BudgetComparisonView } from './budget-comparison-view';
import { BudgetRevisionTimeline } from './budget-revision-timeline';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { BudgetRevision } from '@/types/budget.types';
import { Clock, User, FileText, GitCompare, History } from 'lucide-react';
import { getStatusColor } from '@/lib/status-colors';

interface BudgetHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  revisions: BudgetRevision[];
  projectName: string;
  onRestore?: (revisionId: string) => void;
}

export function BudgetHistoryModal({
  open,
  onOpenChange,
  revisions,
  projectName,
  onRestore,
}: BudgetHistoryModalProps) {
  const [selectedRevisions, setSelectedRevisions] = useState<[string, string] | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  const handleCompare = (rev1: string, rev2: string) => {
    setSelectedRevisions([rev1, rev2]);
    setShowComparison(true);
  };

  const handleRestore = (revisionId: string) => {
    if (window.confirm('Are you sure you want to restore this budget revision? This will create a new revision based on the selected version.')) {
      onRestore?.(revisionId);
    }
  };

  const getStatusBadge = (status: string) => {
    const label = status.charAt(0).toUpperCase() + status.slice(1);
    return <Badge className={getStatusColor(status)}>{label}</Badge>;
  };

  if (showComparison && selectedRevisions) {
    const rev1 = revisions.find(r => r.revisionId === selectedRevisions[0]);
    const rev2 = revisions.find(r => r.revisionId === selectedRevisions[1]);

    if (rev1 && rev2) {
      return (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Compare Budget Revisions</DialogTitle>
              <DialogDescription>
                Comparing {rev1.revisionNumber} vs {rev2.revisionNumber}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Button
                variant="outline"
                onClick={() => setShowComparison(false)}
              >
                ← Back to History
              </Button>
              <BudgetComparisonView
                revision1={rev1}
                revision2={rev2}
              />
            </div>
          </DialogContent>
        </Dialog>
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Budget Revision History</DialogTitle>
          <DialogDescription>
            View and compare all budget revisions for {projectName}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="list" className="w-full">
          <TabsList>
            <TabsTrigger value="list">
              <FileText className="h-4 w-4 mr-2" />
              Revision List
            </TabsTrigger>
            <TabsTrigger value="timeline">
              <History className="h-4 w-4 mr-2" />
              Timeline
            </TabsTrigger>
          </TabsList>

          <TabsContent value="list" className="space-y-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Revision</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Changed By</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Changes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {revisions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        No revisions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    revisions.map((revision, index) => (
                      <TableRow key={revision.revisionId || revision.id}>
                        <TableCell className="font-medium">
                          {revision.revisionNumber}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            {new Date(revision.createdAt || revision.date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {revision.createdBy}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {revision.reason || '-'}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(revision.status)}
                        </TableCell>
                        <TableCell>
                          {revision.changes && (
                            <div className="text-sm space-y-1">
                              {revision.changes.totalCostChange !== 0 && (
                                <div className={revision.changes.totalCostChange > 0 ? 'text-error-600' : 'text-success-600'}>
                                  Cost: {revision.changes.totalCostChange > 0 ? '+' : ''}
                                  £{Math.abs(revision.changes.totalCostChange).toLocaleString()}
                                </div>
                              )}
                              {revision.changes.totalRevenueChange !== 0 && (
                                <div className={revision.changes.totalRevenueChange > 0 ? 'text-success-600' : 'text-error-600'}>
                                  Revenue: {revision.changes.totalRevenueChange > 0 ? '+' : ''}
                                  £{Math.abs(revision.changes.totalRevenueChange).toLocaleString()}
                                </div>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {index < revisions.length - 1 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCompare(
                                  revision.revisionId || String(revision.id),
                                  revisions[index + 1].revisionId || String(revisions[index + 1].id)
                                )}
                              >
                                <GitCompare className="h-4 w-4 mr-1" />
                                Compare
                              </Button>
                            )}
                            {revision.status !== 'current' && onRestore && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRestore(revision.revisionId || String(revision.id))}
                              >
                                Restore
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {revisions.length > 1 && (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="text-sm text-muted-foreground">
                  <strong>{revisions.length}</strong> revisions total
                </div>
                <div className="text-sm text-muted-foreground">
                  Current version: <strong>{revisions[0]?.revisionNumber}</strong>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="timeline">
            <BudgetRevisionTimeline revisions={revisions} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
